import React, { useState, useEffect } from "react";
import { Facebook, Instagram, Linkedin, Youtube } from "lucide-react";
import logo from "../../Assets/Logos/footerlogo.png";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const Footer = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const currentLanguage = i18n.language || "en";

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/categories/get-all-categories`,
        );
        setCategories(response.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  // Helper to get localized name
  const getLocalizedName = (category) => {
    if (!category || !category.name) return "";

    if (typeof category.name === "string") {
      return category.name;
    }

    if (typeof category.name === "object") {
      return (
        category.name[currentLanguage] ||
        category.name.en ||
        category.name.hi ||
        category.name.es ||
        category.name.zh ||
        "Category"
      );
    }

    return "Category";
  };

  // Shared navigation helper with smooth scroll to top
  const handleNavigate = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="bg-[#211700] text-white">
      {/* Main Footer Content */}
      <div className="px-4 md:px-10 py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand Section */}
          <div className="space-y-4">
            <img src={logo} alt="" />
            <p className="text-[#E3E3E3] text-sm md:text-xl font-philosopher leading-relaxed">
              {t("footer.description")}
            </p>
          </div>

          {/* Important Links */}
          <div>
            <h3 className="text-xl text-[#E3E3E3] font-bold font-philosopher mb-6">
              {t("footer.importantLinks")}
            </h3>
            <ul className="space-y-3">
              <li>
                <div
                  onClick={() => handleNavigate("/privacy-policy")}
                  className="text-[#E3E3E3] hover:text-[#f4d03f] transition-colors text-sm cursor-pointer"
                >
                  {t("footer.privacyPolicy")}
                </div>
              </li>
              <li>
                <div
                  onClick={() => handleNavigate("/terms-conditions")}
                  className="text-[#E3E3E3] hover:text-[#f4d03f] transition-colors text-sm cursor-pointer"
                >
                  {t("footer.termsConditions")}
                </div>
              </li>
              <li>
                <div
                  onClick={() => handleNavigate("/return-policy")}
                  className="text-[#E3E3E3] hover:text-[#f4d03f] transition-colors text-sm cursor-pointer"
                >
                  {t("footer.returnPolicy")}
                </div>
              </li>
              <li>
                <div
                  onClick={() => handleNavigate("/shipping-policy")}
                  className="text-[#E3E3E3] hover:text-[#f4d03f] transition-colors text-sm cursor-pointer"
                >
                  {t("footer.shipingPolicy")}
                </div>
              </li>
              <li>
                <div
                  onClick={() => handleNavigate("/contact")}
                  className="text-[#E3E3E3] hover:text-[#f4d03f] transition-colors text-sm cursor-pointer"
                >
                  {t("footer.contactUs")}
                </div>
              </li>
            </ul>
          </div>

          {/* Categories - Dynamically loaded from API */}
          <div className="text-[#E3E3E3]">
            <h3 className="text-xl font-bold font-philosopher mb-6">
              {t("footer.categories")}
            </h3>
            <ul className="space-y-3">
              {categories.map((item) => (
                <li key={item._id}>
                  <div
                    onClick={() => handleNavigate(`/category/${item._id}`)}
                    className="hover:text-[#f4d03f] transition-colors text-sm cursor-pointer"
                  >
                    {getLocalizedName(item)}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect With Us */}
          <div>
            <h3 className="text-xl font-bold font-philosopher mb-6 text-[#E3E3E3]">
              {t("footer.connectWithUs")}
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-[#E3E3E3] mb-2">
                  {t("footer.writeToUs")}
                </p>
                <div
                  onClick={() =>
                    handleNavigate("mailto:connect@bharatkarigari.com")
                  }
                  className="text-[#E3E3E3] hover:text-[#f4d03f] transition-colors text-sm cursor-pointer"
                >
                  connect@bharatkarigari.com
                </div>
              </div>
              <div>
                <p className="text-sm mb-2">{t("footer.existingClients")}</p>
                <div
                  onClick={() =>
                    handleNavigate("mailto:customercare@bharatkarigari.com")
                  }
                  className="text-[#E3E3E3] hover:text-[#f4d03f] transition-colors text-sm cursor-pointer"
                >
                  customercare@bharatkarigari.com
                </div>
              </div>

              {/* Social Media Icons */}
              <div className="pt-4">
                <h4 className="text-xl text-[#E3E3E3] font-bold font-philosopher mb-4">
                  {t("footer.connectWithUs")}
                </h4>
                <div className="flex gap-4">
                  <div
                    className="hover:text-[#f4d03f] transition-colors"
                    aria-label="Facebook"
                  >
                    <Facebook className="w-6 h-6" />
                  </div>
                  <div
                    className="hover:text-[#f4d03f] transition-colors"
                    aria-label="Instagram"
                  >
                    <Instagram className="w-6 h-6" />
                  </div>
                  <div
                    className="hover:text-[#f4d03f] transition-colors"
                    aria-label="LinkedIn"
                  >
                    <Linkedin className="w-6 h-6" />
                  </div>
                  <div
                    className="hover:text-[#f4d03f] transition-colors"
                    aria-label="YouTube"
                  >
                    <Youtube className="w-6 h-6" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-700">
        <div className="container mx-auto px-6 py-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#E3E3E3]">{t("footer.copyright")}</p>
            <div className="flex items-center">
              <span>{t("footer.designedBy")}</span>
              <button
                onClick={() => {
                  window.open("https://webbocket.com/", "_blank");
                }}
                className="ml-1 text-[#E3E3E3] underline"
              >
                Web_Bocket
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
